import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiX, FiRefreshCw } = FiIcons;

const UpdateNotification = () => {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [swRegistration, setSwRegistration] = useState(null);

  useEffect(() => {
    // Install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Check if already installed or dismissed
      const dismissed = localStorage.getItem('pwa_install_dismissed');
      if (!dismissed && !window.matchMedia('(display-mode: standalone)').matches) {
        setTimeout(() => setShowInstallPrompt(true), 3000);
      }
    };

    // Service worker update
    const handleSWUpdate = (registration) => {
      if (registration.waiting) {
        setSwRegistration(registration);
        setShowUpdatePrompt(true);
      }
    };

    // Check for updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then(registration => {
        if (registration) {
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                handleSWUpdate(registration);
              }
            });
          });
        }
      });
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }
    
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleUpdate = () => {
    if (swRegistration && swRegistration.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      swRegistration.waiting.addEventListener('statechange', (e) => {
        if (e.target.state === 'activated') {
          window.location.reload();
        }
      });
    }
    setShowUpdatePrompt(false);
  };

  const dismissInstall = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('pwa_install_dismissed', 'true');
  };

  return (
    <>
      {/* Install Prompt */}
      <AnimatePresence>
        {showInstallPrompt && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-4 left-4 right-4 z-50"
          >
            <div className="bg-blue-600 text-white rounded-xl shadow-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="bg-blue-500 p-2 rounded-lg">
                  <SafeIcon icon={FiDownload} className="text-xl" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">Instalar Aplicación</h4>
                  <p className="text-sm text-blue-100 mb-3">
                    Instala la app para acceso rápido y uso sin conexión
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleInstall}
                      className="bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors"
                    >
                      Instalar
                    </button>
                    <button
                      onClick={dismissInstall}
                      className="text-blue-100 hover:text-white px-4 py-2 text-sm transition-colors"
                    >
                      Ahora no
                    </button>
                  </div>
                </div>
                <button
                  onClick={dismissInstall}
                  className="text-blue-200 hover:text-white p-1"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Update Prompt */}
      <AnimatePresence>
        {showUpdatePrompt && (
          <motion.div
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            className="fixed top-4 left-4 right-4 z-50"
          >
            <div className="bg-green-600 text-white rounded-xl shadow-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="bg-green-500 p-2 rounded-lg">
                  <SafeIcon icon={FiRefreshCw} className="text-xl" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold">Actualización Disponible</h4>
                  <p className="text-sm text-green-100">
                    Nueva versión lista para instalar
                  </p>
                </div>
                <button
                  onClick={handleUpdate}
                  className="bg-white text-green-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-50 transition-colors"
                >
                  Actualizar
                </button>
                <button
                  onClick={() => setShowUpdatePrompt(false)}
                  className="text-green-200 hover:text-white p-1"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default UpdateNotification;